// Tour Definitions - All guided tours with steps and targets
// This is the single source of truth for all tour content
// All targets use [data-tour="..."] selectors exclusively

import { effect } from '@preact/signals'
import { navigate } from 'wouter-preact/use-browser-location'
import { navigateToPage, navigateToBenchmarkingStep, scrollToElement, waitForElement } from './tourNavigation.js'
import { clickElement } from './tourInteractions.js'
import { activeTour } from './tourState.js'
import { bumpIonIntroGen } from './ionIntroGen.js'
import { sidebarVisible, activeSection, switchSection, filteredCells } from '../state/globalSignals.js'
import { selectedFormats, selectedTechnologies, selectedChemistries } from '../state/benchmarkingWorkflowState.js'
import { compareCells, panelMode } from '../state/filterStore.js'
import { radarActivePlotIndex } from '../state/radarState.js'
import { snapshotExplorerState, restoreExplorerState } from '../state/commandLayer.js'
import { setIonSurface, chatMessages } from '../state/chatStore.js'
import {
  CANONICAL_INTRO_PROMPT,
  fireIntroTurn,
  awaitIntroTurnOutcome,
  introTurnOutcome,
  introTurnFrozenCount,
  abortIonIntroTurnIfLive,
  clearPendingIntroDraft,
  hasIntroTurnFiredThisRun,
} from './ionIntroTurn.js'

/**
 * Task 1 (defect A2): the hero step's caption, as a pure function of the
 * three facts that decide it. Extracted from the step's `content()` so it
 * can be unit-tested without rendering anything.
 *
 * `frozenCount` is the count ionIntroTurn.js captured at the exact moment
 * the turn settled 'driven' (see introTurnFrozenCount()); `liveCount` is
 * whatever filteredCells reads NOW. They can diverge on a Back re-entry
 * after the user has filtered further themselves — the caption must never
 * present the CURRENT count as evidence of what Ion did. When they diverge,
 * this drops to the no-count "driven" variant rather than re-reading the
 * live number — the honest choice: Ion did drive the grid, but citing a
 * number that includes the user's own later edits would misattribute their
 * work to Ion. Do not "fix" this by substituting `liveCount` back in.
 *
 * @param {object} o
 * @param {boolean} o.drove
 * @param {number|null} o.frozenCount
 * @param {number} o.liveCount
 */
export function introDriveCaption({ drove, frozenCount, liveCount }) {
  if (drove && frozenCount != null && frozenCount > 0 && liveCount === frozenCount) {
    return `
      <p>That was me — I switched the basis, set the filters and re-sorted the grid, live.</p>
      <p class="tour-step-hint">✦ <span class="tour-step-figure">${frozenCount}</span> cells match</p>
    `
  }
  if (drove) {
    return `<p>That was me driving the Explorer — I set those filters myself, live.</p>`
  }
  return `
    <p>That's the idea: you ask in plain language, and I drive the Explorer for you.</p>
    <p class="tour-step-hint">Give it a try below.</p>
  `
}

// Task 3 handover chips: the 3 ACTION prompts offered on intro-handover.
// Exported so ChatEmptyState's docked-empty host (IonSurface.jsx) can render
// them as the empty-state prompt-chip override while ion-intro is active —
// clicking one sends it through ChatEmptyState's existing sendChatMessage
// path, which is what flips chatMessages non-empty and (via intro-handover's
// canAdvance/autoAdvance below) advances the tour. No tour-completion logic
// lives on the chip itself.
export const ION_INTRO_HANDOVER_PROMPTS = [
  'Filter to LFP cells',
  'Compare the top 3',
  'Take me to the best cell for an e-bike',
]

// Explorer-state snapshot taken right before the ion-intro demo (intro-greet
// beforeShow) mutates filters/sort — restored on tour end (complete OR skip)
// via restoreIonIntroBaseline() below, so the user never lands in the demo's
// 21700/energy>=250/sorted-by-power state. Module-level `let`, not a
// stack: ion-intro's beforeShow guarantees at most one demo in flight at a
// time (see AuthenticatedApp.jsx / HelpMenuButton.jsx "Meet Ion", Task 2).
let _ionIntroBaseline = null

// M3: the pathname+search the user was on when intro-greet's beforeShow
// navigated them to /explorer. snapshotExplorerState()'s snapshot only
// carries the `?view=` query param, not the pathname (it's an Explorer-only
// snapshot — commandLayer.js's shared snapshot shape is intentionally NOT
// changed for this onboarding-only need), so a "Meet Ion" replay started
// from e.g. /compare used to land the user back on /explorer after
// restoreIonIntroBaseline(), not where they actually were. Captured
// alongside `_ionIntroBaseline`, same guard (only when `_ionIntroBaseline`
// is still null), so a Back re-entry into intro-greet doesn't overwrite it
// with '/explorer' itself.
let _ionIntroReturnPath = null

// C3: abort generation counter — lives in ./ionIntroGen.js (dependency-free)
// because tourState.js's endTour() bumps it too and this module imports
// tourState.js; a counter declared here was reached through a dynamic import
// that could resolve mid-evaluation (TDZ ReferenceError, CI pipeline 2191).
// Bumped by endTour() whenever ion-intro is the active tour being torn down
// (skip, ESC, or completion), and by intro-greet's / intro-handover's own
// beforeShow (R2, below) on every entry.
// Pre-S4, intro-drive's beforeShow captured this at the start of its 3
// scripted delays and re-checked it after EACH await, so a skip/end mid-demo
// stopped the remaining scripted commandLayer calls from firing after the
// baseline had already been restored. S4 replaced that scripted chain with a
// single real turn (ionIntroTurn.js) — the equivalent race was closed by
// abortIonIntroTurnIfLive() (see F3). S4 fix-wave, Fix B: intro-handover's
// own beforeShow no longer calls that abort (the turn is let run to
// completion there instead — see that beforeShow) — the only remaining
// caller is restoreIonIntroBaseline()'s skip/ESC-before-handover teardown
// path. Not re-checked inside intro-drive itself. Left in place: it still
// guards the OTHER awaited section
// (navigateToPage in intro-greet is not gated by it, but bumping on every
// (re-)entry remains cheap/harmless insurance against future step changes).

// I3: the message count captured when intro-handover is entered. First-run
// this is 0 (no prior conversation); on a replay ("Meet Ion" from the Help
// menu after the user already chatted) it's whatever chatMessages already
// holds. intro-handover's canAdvance compares against THIS, not a bare
// `> 0`, so an existing conversation doesn't insta-advance the step before
// the user has done anything in THIS demo run.
let _handoverEntryMsgCount = 0

// FIX 4 (S4 fix-wave): the per-run "already fired" guard for the hero turn
// lives in ionIntroTurn.js, next to the turn itself — see the long comment
// there for why it cannot live in this module (import cycle with
// tourState.js) and for the two mechanisms that failed before.
// Re-exported here because the tour tests re-arm it between cases.
export { resetIntroTurnRun as resetIonIntroTurnRun } from './ionIntroTurn.js'

/**
 * Tour Step Definition
 * @typedef {Object} TourStep
 * @property {string} id - Unique step ID
 * @property {string} title - Step title (shown in tooltip)
 * @property {string} content - Step description (HTML supported)
 * @property {string|null} target - CSS selector for highlighted element ([data-tour="..."]) or null for centered
 * @property {string} position - Tooltip position: 'top' | 'bottom' | 'left' | 'right' | 'center'
 * @property {Function} [beforeShow] - Async function to run before showing step (navigation, state changes)
 * @property {boolean} [highlight] - Whether to spotlight target (default: true)
 * @property {boolean} [autoAdvance] - Auto-advance to next step when canAdvance() returns true (default: false)
 * @property {string} [highlightTarget] - CSS selector for element to pulse-highlight (orange glow animation)
 * @property {boolean} [showProgress] - Show "Step X of Y" (default: true)
 * @property {Object} [buttons] - Custom button config
 */

/**
 * Tour Definition
 * @typedef {Object} Tour
 * @property {string} id - Unique tour ID
 * @property {string} name - Display name
 * @property {string} description - Short description
 * @property {string} icon - Icon name (from Icons.jsx)
 * @property {TourStep[]} steps - Array of tour steps
 */

export const TOURS = {
  /**
   * WELCOME TOUR (NEW)
   * Quick orientation for new users: navigation, controls, sidebar
   */
  welcome: {
    id: 'welcome',
    name: 'Welcome Tour',
    description: 'Get oriented with the main navigation and controls',
    icon: 'IconHome',
    steps: [
      {
        id: 'welcome-intro',
        title: 'Welcome to Batemo Insights',
        content: `
          <p>Your platform for battery cell research and analysis. Batemo Insights gives you access to
          <strong>100+ battery cells</strong> with comprehensive specifications, performance data, and comparison tools.</p>
          <p>This quick tour shows you the main areas and controls. It takes about 30 seconds.</p>
        `,
        target: null,
        position: 'center',
        highlight: false,
        beforeShow: async () => {
          await navigateToPage('/explorer?view=grid', '[data-tour="welcome-nav-tabs"]')
        },
        buttons: {
          next: 'Show me around',
          skip: 'Skip Tour'
        }
      },
      {
        id: 'welcome-nav',
        title: 'Main Navigation',
        content: `
          <p>These tabs are your main navigation across the platform:</p>
          <ul class="tour-step-list">
            <li><strong>Cell Explorer</strong> -- Browse and filter all cells in grid, table, or graph view</li>
            <li><strong>Cell Selection</strong> -- Find the best cell for your requirements in a guided 3-step workflow</li>
            <li><strong>Compare</strong> -- Analyze up to 5 cells side-by-side with automatic best/worst highlighting</li>
          </ul>
        `,
        target: '[data-tour="welcome-nav-tabs"]',
        position: 'bottom'
      },
      {
        id: 'welcome-controls',
        title: 'Basis and Domain Controls',
        content: `
          <p>These global controls affect how values are displayed across the entire platform:</p>
          <ul class="tour-step-list">
            <li><strong>Basis:</strong> Switch between Absolute (Wh, W), Gravimetric (Wh/kg, W/kg), and Volumetric (Wh/L, W/L) values</li>
            <li><strong>Domain:</strong> Toggle between Energy/Power and Capacity/Current representations</li>
          </ul>
          <p class="tour-step-hint">Gravimetric is most useful for weight-sensitive applications like EVs.</p>
        `,
        target: '[data-tour="welcome-global-controls"]',
        position: 'bottom'
      },
      {
        id: 'welcome-sidebar',
        title: 'Sidebar Filters',
        content: `
          <p>The sidebar provides powerful filters to narrow down cells by chemistry, format, manufacturer,
          and performance ranges.</p>
          <p>Filters use OR logic within categories -- selecting both NMC and LFP shows cells with either chemistry.</p>
          <p class="tour-step-hint">The sidebar stays synchronized across all views.</p>
        `,
        target: '[data-tour="welcome-sidebar"]',
        position: 'right',
        beforeShow: async () => {
          sidebarVisible.value = true
          await new Promise(r => setTimeout(r, 450))
          await waitForElement('[data-tour="welcome-sidebar"]')
        }
      }
    ]
  },

  /**
   * ION INTRO TOUR (NEW)
   * First-run hero moment: Ion live-drives the Cell Explorer in front of the
   * user (real commandLayer calls, source:'ion') before handing over. This is
   * a SCRIPTED DEMONSTRATION — never a fabricated chat reply — and it drives
   * the SAME command-layer functions a real Ion tool-call uses, so it also
   * triggers the action-echo (IonActionEcho, Task 4) for free.
   * Gated to Ion-access users only; falls back to `welcome` otherwise
   * (see AuthenticatedApp.jsx trigger switch, Task 2).
   */
  'ion-intro': {
    id: 'ion-intro',
    name: 'Meet Ion',
    description: 'Watch Ion drive the Cell Explorer, then try it yourself',
    icon: 'IconIonBolt',
    steps: [
      {
        id: 'intro-greet',
        title: "I'm Ion",
        // S4/Step 10: name the example explicitly, and interpolate the SAME
        // constant that fireIntroTurn actually sends — the copy can never
        // drift from what is really dispatched.
        // Part C (4 steps -> 3): the preview/real-data disclaimer used to
        // live on the now-removed intro-outro (the LAST step) — expectations
        // belong at the START, not the end. Folded into the second
        // paragraph rather than appended as a 4th one, so the step still
        // reads as one held thought ("preview, but real — here's a taste")
        // instead of a bolted-on caveat.
        content: `
          <p>I'm Ion. I don't just answer questions — I can drive this app for you. Watch.</p>
          <p>Preview — my toolset is still growing, but what I show you is always real data. Here's a question you might ask me — watch what happens:</p>
          <p class="tour-step-hint">"${CANONICAL_INTRO_PROMPT}"</p>
        `,
        target: null,
        position: 'center',
        highlight: false,
        beforeShow: async () => {
          // R2 hardening: bump the abort generation on every (re-)entry
          // into the tour's first step too, not just at intro-handover —
          // cheap and harmless (no demo is pending yet the first time
          // through), and guards the same class of double-click/stray-
          // advance race for any future step ordering change.
          bumpIonIntroGen()
          // M3: capture the pre-navigation location BEFORE the navigate below
          // moves it to /explorer — guarded the same way as the baseline
          // snapshot two lines down, so a Back re-entry doesn't overwrite the
          // real starting point with '/explorer' itself.
          if (_ionIntroBaseline == null) {
            _ionIntroReturnPath = window.location.pathname + window.location.search
          }
          await navigateToPage('/explorer?view=grid', '[data-tour="explorer-view-switcher"]')
          // Capture the pre-demo baseline BEFORE setIonSurface — restored at
          // intro-handover entry (or, as a pre-handover-skip fallback, on
          // tour complete/skip via restoreIonIntroBaseline() in
          // tourState.js). Guarded (I5): without this, Back from intro-drive
          // to intro-greet re-runs this beforeShow and would re-snapshot the
          // demo's OWN already-mutated state as if it were the pre-demo
          // baseline, so a later restore "restores" into the demo state
          // instead of the user's real starting point.
          if (_ionIntroBaseline == null) {
            _ionIntroBaseline = snapshotExplorerState()
          }
          setIonSurface('docked')
        },
        buttons: {
          next: 'Show me',
          skip: 'Skip'
        }
      },
      {
        id: 'intro-drive',
        title: 'Here’s what that looks like',
        // S4/Step 8: honest caption. The old static text claimed "I just set
        // 2 filters" unconditionally — with a REAL turn that claim must be
        // earned. The count is only allowed to REFINE the caption (a
        // triumphant "N cells" over an empty grid would be worse than no
        // caption at all) — the actual success signal is introTurnOutcome().
        // Task 1 (defect A2): the count itself is READ ONCE, frozen at the
        // moment the turn settled (introTurnFrozenCount()) — see
        // introDriveCaption()'s doc comment above for why the live count
        // cannot be trusted on a Back re-entry.
        content: () =>
          introDriveCaption({
            drove: introTurnOutcome() === 'driven',
            frozenCount: introTurnFrozenCount(),
            liveCount: filteredCells.value.length,
          }),
        // I2: target the WHOLE content area (sidebar + grid), not just the
        // filter panel — so the overlay's cutout keeps the grid visible
        // while it live-reflows instead of dimming it behind 65% black for
        // the whole 2.1s. The filter panel still gets its own pulse-glow via
        // highlightTarget below.
        target: '[data-tour="explorer-content-area"]',
        // R1: I2 wasn't actually fixed by the target change alone — the
        // cutout only takes effect AFTER beforeShow resolves (TourOverlay.jsx
        // resolves the target + setTargetRect at the END of setupStep), but
        // THIS step's beforeShow IS the demo. Without preRevealTarget, the
        // whole demo still plays behind the previous (centered) step's
        // full-screen dim. preRevealTarget resolves the cutout BEFORE
        // beforeShow runs, so the grid is already visible when the demo
        // starts reflowing it.
        preRevealTarget: true,
        position: 'right',
        // Task 2 (defect A1): 'right' of the content area is exactly where
        // the Ion panel sits (docked, right-anchored) — the card must not
        // land on top of the very panel whose answer this step is about.
        // TourOverlay.jsx constrains shift/flip to the space left of the
        // panel when this is set; see resolveAvoidPanelBoundary() there for
        // the degenerate-geometry fallback.
        avoidPanel: true,
        highlightTarget: '[data-tour="explorer-filter-panel"]',
        // Task 5 (Part B): give the auto-sent example question the SAME
        // corner-bracket reticle as the primary target — one highlight
        // vocabulary, not a tooltip reticle here and a chat-bubble glow
        // there. TourOverlay.jsx finds the bubble via the existing
        // meta.example flag's rendered chip, not a new marker.
        markExampleBubble: true,
        // THE HERO MOMENT — S4/F2: fires a REAL /chat/stream turn instead of
        // scripting the command layer. The model calls the Explorer tools
        // itself; commandProvenance flipping to source:'ion' is the ONLY
        // success signal (see ionIntroTurn.js's module doc for why). There is
        // deliberately NO scripted fallback on timeout/error/no-drive — the
        // safety net degrades to "the tour proceeds with a softer caption",
        // because silently re-running the old script would reintroduce the
        // exact fake this slice removes (F4).
        beforeShow: async () => {
          // R3: re-capture a fresh baseline on RE-ENTRY (intro-handover
          // no longer restores on its own entry — BA fix-wave decision, see
          // intro-handover's beforeShow below — but still NULLS it, so Back
          // would otherwise apply on top of a null baseline). Unchanged
          // from S1.
          if (_ionIntroBaseline == null) {
            _ionIntroBaseline = snapshotExplorerState()
          }
          // Open the filter panel so the user can SEE Ion's changes land.
          // This is stage-setting, not a fake drive — every filter/sort/basis
          // change below comes from Ion's own tool calls.
          sidebarVisible.value = true
          // FIX 4 (S4 fix-wave): only fire the real turn ONCE per tour run.
          // Only the user's first-ever turn is token-exempt — every one
          // after that is real Bedrock spend on their own budget (see
          // service.py's uq_ion_llm_calls_user_exempt) — so re-running this
          // beforeShow on a Back re-entry must NOT fire a second paid turn
          // (and must not leak a second watcher effect either). On re-entry
          // the step's content() re-derives its caption from the already-
          // settled introTurnOutcome() (module state, read fresh on every
          // render) — nothing else to do here.
          if (hasIntroTurnFiredThisRun()) return
          // S4/F2+F4: fire a REAL /chat/stream turn instead of scripting the
          // command layer. There is deliberately NO scripted fallback — the
          // safety net degrades to "the tour proceeds with a softer caption",
          // because silently re-running the old script would reintroduce the
          // exact fake this slice removes.
          fireIntroTurn({ contextHint: null })
          await awaitIntroTurnOutcome()
        },
        buttons: {
          next: 'Nice — what else?'
        }
      },
      {
        id: 'intro-handover',
        // Task 5 (defect: "Now you try" told the user nothing). The title
        // now names the moment; the content below names the ACTION and the
        // promise, instead of restating the title — that restatement is
        // exactly why BA reported not knowing what to do.
        title: 'Your turn',
        content: `
          <p>Pick one below, or type your own question. I change the Explorer for real — I don't just describe it.</p>
        `,
        // C1: the cutout must cover BOTH the chips (ChatEmptyState, rendered
        // in __body) AND the composer (__footer) — targeting only the input
        // (as before) left the chips outside the cutout, where the overlay's
        // click-to-skip swallowed every chip click. See IonSurface.jsx for
        // the `ion-intro-handover` anchor spanning both.
        target: '[data-tour="ion-intro-handover"]',
        position: 'left',
        // C1: without this, the SVG overlay's own onClick=handleSkip fires
        // for any click inside the cutout (guided-tour.css's
        // `.tour-overlay-svg path { pointer-events:all }`) — the activation
        // moment itself was click-dead. `interactive:true` switches the
        // overlay to `.tour-overlay-svg--interactive`, whose CSS rule
        // (`pointer-events:visibleFill`) makes the cutout genuinely
        // click-through, same as the benchmarking tour's interactive steps.
        interactive: true,
        beforeShow: async () => {
          // R2: bump the abort generation BEFORE restoring. The tooltip is
          // invisible (opacity 0) but not pointer-events:none while a step
          // is transitioning, and DOM-level double-clicks (or Enter while
          // focus is still on the previous step's Next button) can advance
          // straight past intro-drive into intro-handover WHILE intro-
          // drive's beforeShow is still mid-flight (its own gen check only
          // fires at its next await, which is 700ms out). Bumping here
          // ensures that pending chain sees the generation change and bails
          // on its NEXT check, instead of applying demo filters AFTER the
          // baseline was already restored+nulled below (which would strand
          // the user in that partial demo state permanently, since
          // restoreIonIntroBaseline() is then a no-op for the rest of the
          // tour).
          bumpIonIntroGen()
          // S4 fix-wave (BA decision, Fix B): do NOT abort the onboarding
          // stream here anymore. The old S4/F3 abort made Ion's own caption
          // ("That was me — I switched the basis, set the filters and
          // re-sorted the grid, live.") a lie half the time — the very next
          // render appended "Stopped — partial response kept above." right
          // underneath it. Worse, a CLIENT-SIDE abort skips the server's
          // usage logging entirely, so the exempt free-turn row (see
          // service.py's uq_ion_llm_calls_user_exempt) never gets written —
          // the "one free turn per user" bookkeeping silently only fired if
          // the user waited the turn out. Let the turn run to completion
          // instead: the handover chips (IonIntroHandoverChips.jsx) and
          // ChatEmptyState both gate sending on chatStreaming, so a still-
          // live turn can never collide with a second one fired from here.
          //
          // A DRAFT that never even started firing (e.g. stuck behind an
          // exhausted-quota guard) still needs clearing, though — left in
          // `autoSendDraft` it would otherwise fire later on whatever page
          // the user has since navigated to. clearPendingIntroDraft() does
          // ONLY that (no stopChatStream call) — see ionIntroTurn.js. The
          // full abortIonIntroTurnIfLive() (draft clear + stream stop) stays
          // reserved for restoreIonIntroBaseline()'s skip/ESC-before-
          // handover teardown path, where the user genuinely bailed instead
          // of taking over.
          clearPendingIntroDraft()
          // FIX 2 (S4 fix-wave, BA decision): do NOT restore the pre-demo
          // baseline here anymore. Restoring used to be justified by "it was
          // only a script" — since S4 the drive step is a REAL Ion turn that
          // lands in the chat transcript with its own caption ("That was me
          // — I switched the basis, set the filters and re-sorted the grid,
          // live."), and reverting it a moment later is dishonest and undoes
          // real work Ion just did. The driven state now STAYS on screen
          // across the handover.
          //
          // The baseline/return-path must still be NULLED here, though —
          // this is the important part, not a no-op cleanup. Without it,
          // completeTour()/skipTour()'s restoreIonIntroBaseline() fallback
          // (kept for the skip/ESC-before-handover teardown path, see
          // restoreIonIntroBaseline()'s doc comment) would still fire at
          // tour end and restore the PRE-TOUR baseline over the user's OWN
          // first action (e.g. the LFP filter they just set via a handover
          // chip) — reintroducing exactly the bug (C4) that restore-at-
          // handover originally existed to fix. Nulling here makes that
          // fallback restore a no-op for the rest of this run, same as
          // before — only the RESTORE call itself was removed.
          _ionIntroBaseline = null
          _ionIntroReturnPath = null
          // I3: capture the message count AT ENTRY. First-run this is 0; on
          // a replay ("Meet Ion" from the Help menu after the user already
          // chatted) chatMessages is non-empty already, and canAdvance below
          // must require a NEW message past this baseline instead of a bare
          // `.length > 0` (which insta-advanced the step ~0.7s in, before
          // the chips — ChatEmptyState only renders when empty — ever
          // appeared).
          _handoverEntryMsgCount = chatMessages.value.length
        },
        // Auto-advance on the user's FIRST sent message THIS RUN — chip click
        // or a typed message both push into chatMessages via sendChatMessage,
        // so either counts as the activation event. See IonSurface.jsx for
        // the 3 handover chips rendered as the docked empty-state's
        // prompt-chip override during this step (ION_INTRO_HANDOVER_PROMPTS
        // above).
        // Part C (4 steps -> 3): this is now the LAST step. TourOverlay
        // derives `isLastStep` from `tour.steps.length`, so its default
        // Next-button label already becomes "Finish" here with no change
        // needed — and autoAdvance's `nextStep(tour.steps.length)` call
        // (TourOverlay.jsx) already routes into tourState.js's
        // `completeTour()` once `currentStep` is the last index, so the
        // user's first sent message now completes the tour directly instead
        // of advancing into the removed intro-outro.
        canAdvance: () => chatMessages.value.length > _handoverEntryMsgCount,
        autoAdvance: true
      }
      // Part C (4 steps -> 3): intro-outro removed entirely — its app-tour
      // offer was REDUNDANT (HelpMenuButton.jsx's "Guided Tours" dropdown
      // already lists every tour, including "welcome"), and the step
      // interrupted immediately after the user's first successful action to
      // deliver a caveat, competing with the moment it should have
      // celebrated. The preview caveat moved to intro-greet (above); the
      // app-tour offer needed no new home — it was already reachable. Its
      // dead-only-here CSS hook (`tour-step-cta`, never styled) and the
      // `data-tour="ion-sneak-badge"` DOM anchor (ChatPanelHeader.jsx) were
      // removed alongside it — grepped first to confirm no other step or
      // test still needed either.
    ]
  },

  /**
   * EXPLORER GRID TOUR
   * Deep dive into grid view features: filtering, sorting, comparison
   */
  'explorer-grid': {
    id: 'explorer-grid',
    name: 'Cell Explorer',
    description: 'Browse cells with filters, sorting, and comparison tools',
    icon: 'IconGrid',
    steps: [
      {
        id: 'explorer-intro',
        title: 'Cell Explorer',
        content: `
          <p>The Cell Explorer is your starting point for browsing <strong>100+ battery cells</strong>
          with powerful filtering and comparison tools.</p>
          <p>You can switch between Grid, Table, and Graph views depending on how you prefer to analyze the data.</p>
        `,
        target: null,
        position: 'center',
        highlight: false,
        beforeShow: async () => {
          await navigateToPage('/explorer?view=grid', '[data-tour="explorer-view-switcher"]')
        },
        buttons: {
          next: 'Show me the tools',
          skip: 'Skip Tour'
        }
      },
      {
        id: 'explorer-view-switch',
        title: 'View Switcher',
        content: `
          <p>Switch between three ways to view cells:</p>
          <ul class="tour-step-list">
            <li><strong>Grid:</strong> Visual cards with key metrics and cell images</li>
            <li><strong>Table:</strong> Sortable columns for efficient data comparison</li>
            <li><strong>Graph:</strong> Custom scatter plots with any KPI combination</li>
          </ul>
          <p class="tour-step-hint">Each view shares the same filters, so switching preserves your selection.</p>
        `,
        target: '[data-tour="explorer-view-switcher"]',
        position: 'bottom'
      },
      {
        id: 'explorer-filters',
        title: 'Filter Panel',
        content: `
          <p>Narrow down cells using categorical and range filters:</p>
          <ul class="tour-step-list">
            <li><strong>Chemistry:</strong> NMC, NCA, LFP, and more</li>
            <li><strong>Format:</strong> Cylindrical (18650, 21700), pouch, prismatic</li>
            <li><strong>Manufacturer:</strong> Samsung, LG, CATL, Panasonic, etc.</li>
            <li><strong>Performance ranges:</strong> Energy, power, capacity thresholds</li>
          </ul>
          <p class="tour-step-hint">Filters use OR logic within categories -- selecting NMC and LFP shows cells with either chemistry.</p>
        `,
        target: '[data-tour="explorer-filter-panel"]',
        position: 'right',
        beforeShow: async () => {
          sidebarVisible.value = true
          await new Promise(r => setTimeout(r, 450))
          await waitForElement('[data-tour="explorer-filter-panel"]')
        }
      },
      {
        id: 'explorer-sort',
        title: 'Sort Controls',
        content: `
          <p>Sort cells by name, energy, power, or other metrics. The sort direction toggles with each click.</p>
          <p>Combined with the basis switcher, you can sort by gravimetric energy density (Wh/kg), volumetric
          power density (W/L), or any other normalized metric.</p>
        `,
        target: '[data-tour="explorer-sort-controls"]',
        position: 'bottom'
      },
      {
        id: 'explorer-card',
        title: 'Cell Cards',
        content: `
          <p>Each card displays key metrics in your selected basis, along with the cell image and manufacturer.</p>
          <p>The metrics automatically update when you switch between Absolute, Gravimetric, and Volumetric values.</p>
          <p class="tour-step-hint">Click any card to open the detailed cell view with specifications across 6 sections.</p>
        `,
        target: '[data-tour="explorer-cell-card"]',
        position: 'right',
        beforeShow: async () => {
          await waitForElement('[data-tour="explorer-cell-card"]', { timeout: 8000 })
        }
      },
      {
        id: 'explorer-compare',
        title: 'Compare Button',
        content: `
          <p>Add cells to your comparison cart for side-by-side analysis. You can compare up to <strong>5 cells</strong> at once.</p>
          <p>Once you have added at least 2 cells, switch to the Compare tab for a detailed breakdown of every metric.</p>
          <p class="tour-step-hint">Click the compare button on any cell card to add it to your cart.</p>
        `,
        target: '[data-tour="explorer-compare-btn"]',
        position: 'bottom'
      }
    ]
  },

  /**
   * GRAPH TOUR
   * Custom scatter plots with guided presets and expert mode
   */
  graph: {
    id: 'graph',
    name: 'Graph View',
    description: 'Create custom scatter plots with any KPI combination',
    icon: 'IconChart',
    steps: [
      {
        id: 'graph-intro',
        title: 'Custom Scatter Plots',
        content: `
          <p>The <strong>Graph view</strong> lets you create custom scatter plots with any KPI combination
          from 50+ metrics across energy, power, capacity, quality, and more.</p>
          <p>Explore relationships between cell properties to identify trends and outliers across the entire database.</p>
        `,
        target: null,
        position: 'center',
        highlight: false,
        beforeShow: async () => {
          await navigateToPage('/explorer?view=graph', '[data-tour="graph-plot-area"]')
          const { appViewMode } = await import('../state/appViewMode.js')
          appViewMode.value = 'cells-graph'
          await waitForElement('[data-tour="graph-plot-area"]', { timeout: 8000 })
        },
        buttons: {
          next: 'Explore the graph',
          skip: 'Skip Tour'
        }
      },
      {
        id: 'graph-plot',
        title: 'Interactive Plot Area',
        content: `
          <p>Each dot represents a battery cell. The plot updates in real-time as you change axes, filters, or visual encoding.</p>
          <ul class="tour-step-list">
            <li><strong>Hover</strong> over a dot to see the cell name and values</li>
            <li><strong>Click</strong> a dot to open the full cell detail view</li>
            <li><strong>Zoom</strong> by scrolling or dragging to select a region</li>
          </ul>
        `,
        target: '[data-tour="graph-plot-area"]',
        position: 'top'
      },
      {
        id: 'graph-mode',
        title: 'Guided vs Expert Mode',
        content: `
          <p>Toggle between two analysis modes:</p>
          <ul class="tour-step-list">
            <li><strong>Guided:</strong> Pre-configured analysis questions organized by category -- ideal for quick exploration</li>
            <li><strong>Expert:</strong> Full control over every axis, scale, and visual parameter -- ideal for custom research</li>
          </ul>
          <p class="tour-step-hint">Start with Guided mode to discover interesting relationships, then switch to Expert for fine-tuning.</p>
        `,
        target: '[data-tour="graph-mode-toggle"]',
        position: 'right',
        beforeShow: async () => {
          const { settingsVisible } = await import('../state/globalSignals.js')
          settingsVisible.value = true
          await new Promise(r => setTimeout(r, 500))
          await waitForElement('[data-tour="graph-mode-toggle"]')
        }
      },
      {
        id: 'graph-presets',
        title: 'Guided Presets',
        content: `
          <p>Guided mode provides pre-configured analyses organized by category:</p>
          <ul class="tour-step-list">
            <li><strong>Performance Analysis:</strong> Gravimetric and volumetric energy vs power (Ragone-style)</li>
            <li><strong>Chemistry Analysis:</strong> Compare cell chemistries across key metrics</li>
            <li><strong>Technology Analysis:</strong> Explore differences between cell technologies</li>
            <li><strong>Supplier Analysis:</strong> Compare manufacturers and their cell portfolios</li>
            <li><strong>Market Evolution:</strong> How cell properties improved over release years</li>
          </ul>
          <p class="tour-step-hint">Each preset configures the optimal axes, colors, and scales for that analysis.</p>
        `,
        target: '[data-tour="graph-presets"]',
        position: 'right',
        beforeShow: async () => {
          const { plotMode } = await import('../state/plotPresets.js')
          plotMode.value = 'user'
          await waitForElement('[data-tour="graph-presets"]')
        }
      },
      {
        id: 'graph-expert',
        title: 'Expert Axis Control',
        content: `
          <p>In Expert mode, you have full control over the plot configuration:</p>
          <ul class="tour-step-list">
            <li><strong>X and Y axes:</strong> Choose any KPI from 50+ metrics</li>
            <li><strong>Z axis:</strong> Enable a third dimension for interactive 3D rotation</li>
            <li><strong>Axis scale:</strong> Switch between linear and logarithmic scales</li>
          </ul>
          <p class="tour-step-hint">3D mode uses Plotly for smooth rotation and tilt interaction.</p>
        `,
        target: '[data-tour="graph-expert-axes"]',
        position: 'right',
        beforeShow: async () => {
          const { plotMode } = await import('../state/plotPresets.js')
          plotMode.value = 'expert'
          await new Promise(r => setTimeout(r, 300))
          await waitForElement('[data-tour="graph-expert-axes"]')
        }
      },
      {
        id: 'graph-visual',
        title: 'Color and Size Encoding',
        content: `
          <p>Customize how cells are visually encoded in the plot:</p>
          <ul class="tour-step-list">
            <li><strong>Color by:</strong> Chemistry, format, manufacturer, or release year to identify clusters</li>
            <li><strong>Size by:</strong> Any numeric KPI -- larger dots indicate higher values</li>
          </ul>
          <p class="tour-step-hint">Try coloring by chemistry to see how NMC, LFP, and NCA cells cluster differently.</p>
        `,
        target: '[data-tour="graph-color-size"]',
        position: 'right'
      }
    ]
  },

  /**
   * BENCHMARKING TOUR
   * Interactive 6-step cell selection workflow
   * Steps 2-4 gate on filter selection, Step 5 is click-through only, Step 6 gates on cell selection
   */
  benchmarking: {
    id: 'benchmarking',
    name: 'Cell Selection Wizard',
    description: 'Find the best cell for your requirements in 3 steps',
    icon: 'IconTarget',
    steps: [
      {
        id: 'benchmarking-intro',
        title: 'Cell Selection Workflow',
        content: `
          <p>The Cell Selection workflow helps you find the <strong>most relevant cell</strong> for your
          specific requirements through a guided selection process.</p>
          <p>You will set filters, define performance targets, and select cells for comparison -- hands-on, step by step.</p>
        `,
        target: '[data-tour="benchmarking-container"]',
        position: 'center',
        highlight: false,
        beforeShow: async () => {
          await navigateToPage('/cell-selection', '[data-tour="benchmarking-container"]')
          navigateToBenchmarkingStep(1)
        },
        buttons: {
          next: 'Walk me through it',
          skip: 'Skip Tour'
        }
      },
      {
        id: 'benchmarking-format',
        title: 'Choose a Cell Format',
        interactive: true,
        canAdvance: () => selectedFormats.value.length > 0,
        skipLabel: 'No preference? Skip \u2192',
        content: () => {
          const count = selectedFormats.value.length
          return count > 0
            ? `<p>You selected <strong>${count} format${count > 1 ? 's' : ''}</strong>. Click <strong>Next</strong> to continue.</p>`
            : `<p>What <strong>cell format</strong> are you looking for? Check one or more options in the panel.</p>
               <ul class="tour-step-list">
                 <li><strong>Cylindrical:</strong> 18650, 21700 -- standard EV and consumer cells</li>
                 <li><strong>Pouch:</strong> Flexible form factor for custom pack designs</li>
                 <li><strong>Prismatic:</strong> Rigid casing, common in automotive modules</li>
               </ul>`
        },
        target: '[data-tour="benchmarking-format"]',
        position: 'right',
        beforeShow: async () => {
          navigateToBenchmarkingStep(1)
          await waitForElement('[data-tour="benchmarking-format"]')
          await scrollToElement('[data-tour="benchmarking-format"]')
        }
      },
      {
        id: 'benchmarking-technology',
        title: 'Choose a Technology',
        interactive: true,
        canAdvance: () => selectedTechnologies.value.length > 0,
        skipLabel: 'No preference? Skip \u2192',
        content: () => {
          const count = selectedTechnologies.value.length
          return count > 0
            ? `<p>You selected <strong>${count} technolog${count > 1 ? 'ies' : 'y'}</strong>. Click <strong>Next</strong> to continue.</p>`
            : `<p>Which <strong>battery technology</strong> fits your application?</p>
               <p class="tour-step-hint">Leave unchecked if you want to see all technologies.</p>`
        },
        target: '[data-tour="benchmarking-technology"]',
        position: 'right',
        beforeShow: async () => {
          navigateToBenchmarkingStep(1)
          await waitForElement('[data-tour="benchmarking-technology"]')
          await scrollToElement('[data-tour="benchmarking-technology"]')
        }
      },
      {
        id: 'benchmarking-chemistry',
        title: 'Choose a Chemistry',
        interactive: true,
        canAdvance: () => selectedChemistries.value.length > 0,
        skipLabel: 'No preference? Skip \u2192',
        content: () => {
          const count = selectedChemistries.value.length
          return count > 0
            ? `<p>You selected <strong>${count} chemistr${count > 1 ? 'ies' : 'y'}</strong>. Click <strong>Next</strong> to continue.</p>`
            : `<p>Which <strong>cell chemistry</strong> do you need?</p>
               <ul class="tour-step-list">
                 <li><strong>NMC:</strong> Balanced energy and power density</li>
                 <li><strong>NCA:</strong> High energy density, used in Tesla vehicles</li>
                 <li><strong>LFP:</strong> Long cycle life, thermally stable</li>
               </ul>`
        },
        target: '[data-tour="benchmarking-chemistry"]',
        position: 'right',
        beforeShow: async () => {
          navigateToBenchmarkingStep(1)
          await waitForElement('[data-tour="benchmarking-chemistry"]')
          await scrollToElement('[data-tour="benchmarking-chemistry"]')
        }
      },
      {
        id: 'benchmarking-performance',
        title: 'Set Performance Requirements',
        interactive: true,
        content: `
          <p>Now define your <strong>target discharge power and required energy</strong>. The system uses DCC-based
          interpolation at 25\u00b0C to calculate achievable performance for each cell.</p>
          <div class="tour-step-highlight">
            <strong>DCC interpolation:</strong> Performance is calculated from actual discharge characteristic curves,
            giving you realistic expectations -- not just nominal datasheet values.
          </div>
          <p class="tour-step-hint">Drag the sliders or requirement lines in the plot to set your thresholds.</p>
        `,
        target: '[data-tour="benchmarking-step2"]',
        position: 'right',
        beforeShow: async () => {
          navigateToBenchmarkingStep(2)
          await waitForElement('[data-tour="benchmarking-step2"]')
          await scrollToElement('[data-tour="benchmarking-step2"]')
        }
      },
      {
        id: 'benchmarking-results-table',
        title: 'Browse Your Results',
        interactive: true,
        tooltipClass: 'tour-tooltip--wide',
        content: `
          <p>Here are all <strong>cells matching your requirements</strong>, ranked by relevance.
          <strong>Click any column header</strong> to sort by Energy, Power, Efficiency, or other metrics.
          Numeric columns default to <strong>highest first</strong> -- click again to reverse.</p>
          <p class="tour-step-hint">Try clicking "Energy" or "Power" to find the top-performing cells.</p>
        `,
        target: '[data-tour="benchmarking-results-table"]',
        position: 'top',
        beforeShow: async () => {
          navigateToBenchmarkingStep(3)
          await waitForElement('[data-tour="benchmarking-results-table"]')
          await scrollToElement('[data-tour="benchmarking-results-table"]')
        }
      },
      {
        id: 'benchmarking-add-to-compare',
        title: 'Add Cells to Compare',
        interactive: true,
        tooltipClass: 'tour-tooltip--wide',
        canAdvance: () => compareCells.value.length > 0,
        skipLabel: 'Skip cell selection \u2192',
        highlightTarget: '.cell-list-table .col-action',
        content: () => {
          const count = compareCells.value.length
          return count > 0
            ? `<p><strong>${count} cell${count > 1 ? 's' : ''}</strong> added to compare. Click <strong>Next</strong> to continue.</p>`
            : `<p>Click the <strong>"+ Add"</strong> buttons in the highlighted <strong>Compare</strong> column to build your shortlist.</p>
               <p class="tour-step-hint">Add at least one cell to continue, or skip this step.</p>`
        },
        target: '[data-tour="benchmarking-results-table"]',
        position: 'top',
        beforeShow: async () => {
          navigateToBenchmarkingStep(3)
          await waitForElement('[data-tour="benchmarking-results-table"]')
        }
      },
      {
        id: 'benchmarking-compare-sidebar',
        title: 'Your Compare List',
        interactive: true,
        content: () => {
          const count = compareCells.value.length
          return count >= 2
            ? `<p>You have <strong>${count} cells</strong> ready for comparison.</p>
               <p>Click <strong>"Compare Cells"</strong> to open the detailed side-by-side analysis.</p>
               <p class="tour-step-hint">You can also search and add more cells here (up to 5).</p>`
            : count === 1
              ? `<p>Your selected cell is here. Add <strong>at least one more</strong> to unlock the comparison view.</p>
                 <p class="tour-step-hint">Use the search bar or go back to the table to add more cells.</p>`
              : `<p>Cells you add will appear in this sidebar. Add cells from the table to get started.</p>`
        },
        target: '[data-tour="benchmarking-compare-sidebar"]',
        position: 'left',
        beforeShow: async () => {
          // Ensure sidebar is visible and in compare mode
          sidebarVisible.value = true
          panelMode.value = 'compare'
          // BenchmarkingWorkflow sets workflow-hidden + adaptive-sidebar--collapsed via classList
          // on every step change — remove them so the sidebar actually expands
          const sidebar = document.querySelector('.adaptive-sidebar')
          if (sidebar) {
            sidebar.classList.remove('workflow-hidden', 'adaptive-sidebar--collapsed')
            sidebar.classList.add('workflow-visible')
          }
          // Wait for sidebar expand animation to complete before positioning
          await new Promise(r => setTimeout(r, 500))
          await waitForElement('[data-tour="benchmarking-compare-sidebar"]')
        },
        buttons: {
          next: 'Finish Tour',
          skip: 'Close'
        }
      }
    ]
  },

  /**
   * SINGLE CELL TOUR (10 steps)
   * Cell detail view with auto-navigated section walkthrough
   * Demo cell: Samsung INR21700-50S
   * Steps 1-7: Intro, section tabs, factsheet, Electrical deep dive (all auto-navigated)
   * Steps 8-9: Thermal + remaining sections (auto-navigated, consolidated)
   * Step 10: Finish with compare prompt
   */
  'single-cell': {
    id: 'single-cell',
    name: 'Cell Detail View',
    description: 'Explore comprehensive cell specifications and performance data',
    icon: 'IconInfo',
    steps: [
      // Step 1: Intro
      {
        id: 'cell-intro',
        title: 'Cell Detail View',
        content: `
          <p>This is the <strong>cell detail view</strong> for the Samsung INR21700-50S -- a widely used reference cell
          in the industry.</p>
          <p>Every cell in the database has comprehensive specifications organized across <strong>6 sections</strong>.
          This tour will walk you through each one -- hands-on.</p>
        `,
        target: '[data-tour="cell-detail-page"]',
        position: 'center',
        highlight: false,
        beforeShow: async () => {
          await navigateToPage(
            '/explorer?cell=samsung-inr21700-50s&section=factsheet',
            '[data-tour="cell-detail-page"]'
          )
          switchSection('factsheet')
        },
        buttons: {
          next: 'Explore the sections',
          skip: 'Skip Tour'
        }
      },
      // Step 2: Section Tabs overview
      {
        id: 'cell-sections',
        title: 'Section Navigation',
        noScroll: true,
        content: `
          <p>These tabs let you switch between <strong>6 data sections</strong>:</p>
          <ul class="tour-step-list">
            <li><strong>Fact Sheet:</strong> Key specifications and cell images</li>
            <li><strong>Electrical:</strong> DCC, voltage, energy, power, impedance</li>
            <li><strong>Thermal:</strong> Heat, temperature, efficiency</li>
            <li><strong>Mechanical:</strong> Dimensions, weight, safety features</li>
            <li><strong>Chemical:</strong> Cathode/anode chemistry and materials</li>
            <li><strong>Quality:</strong> Batch variation and post-formation aging</li>
          </ul>
          <p class="tour-step-hint">We'll visit each section together.</p>
        `,
        target: '[data-tour="cell-section-tabs"]',
        position: 'bottom'
      },
      // Step 3: Fact Sheet (stays at top, noScroll)
      {
        id: 'cell-factsheet',
        title: 'Fact Sheet',
        noScroll: true,
        content: `
          <p>The Fact Sheet gives you a <strong>quick overview</strong> of the cell's key specifications:</p>
          <ul class="tour-step-list">
            <li>Nominal capacity, voltage range, and energy content</li>
            <li>Cell images and dimensional drawings</li>
            <li>Manufacturer specifications and datasheet values</li>
          </ul>
          <p class="tour-step-hint">All values update when you switch the basis (Abs/Grav/Vol) in the global controls.</p>
        `,
        target: '[data-tour="cell-detail-content"]',
        position: 'left',
        beforeShow: async () => {
          switchSection('factsheet')
          await waitForElement('[data-tour="cell-detail-content"]')
        }
      },
      // Step 4: Electrical section (auto-navigated)
      {
        id: 'cell-electrical-click',
        title: 'Electrical Section',
        noScroll: true,
        highlightTarget: '[data-tour="cell-section-tabs"] .section-tab:nth-child(2)',
        content: `<p>We've switched to the <strong>Electrical</strong> section -- the core performance data.</p>
                  <p class="tour-step-hint">This section contains discharge curves, voltage profiles, energy/power data, and impedance measurements.</p>`,
        target: '[data-tour="cell-section-tabs"]',
        position: 'bottom',
        beforeShow: async () => {
          switchSection('electrical')
          await waitForElement('[data-tour="electrical-nav"]')
        }
      },
      // Step 5: Electrical sidebar navigation
      {
        id: 'cell-electrical-nav',
        title: 'Sidebar Navigation',
        noScroll: true,
        content: `
          <p>The sidebar shows <strong>5 electrical categories</strong>, each with live values from the cell data:</p>
          <ul class="tour-step-list">
            <li><strong>Energy/Capacity:</strong> How much energy the cell stores</li>
            <li><strong>Power/Current:</strong> How fast it can deliver energy</li>
            <li><strong>Voltage:</strong> Mean voltage and voltage profiles</li>
            <li><strong>Impedance:</strong> AC impedance with Nyquist and Bode plots</li>
            <li><strong>Resistance:</strong> DC internal resistance</li>
          </ul>
          <p class="tour-step-hint">Each category offers progressive <strong>1D \u2192 2D \u2192 3D</strong> visualization.</p>
        `,
        target: '[data-tour="electrical-nav"]',
        position: 'right',
        beforeShow: async () => {
          if (activeSection.value !== 'electrical') {
            switchSection('electrical')
            await new Promise(r => setTimeout(r, 300))
          }
          await waitForElement('[data-tour="electrical-nav"]')
        }
      },
      // Step 6: Power category (auto-navigated)
      {
        id: 'cell-electrical-power',
        title: 'Power Category',
        noScroll: true,
        highlightTarget: '.electrical-nav .category-card:nth-child(2)',
        content: `<p>We've selected the <strong>Power</strong> category (or Current, depending on your domain setting) to show the cell's power delivery capabilities.</p>
                  <p class="tour-step-hint">The label changes based on your Energy/Power vs Capacity/Current domain setting.</p>`,
        target: '[data-tour="electrical-nav"]',
        position: 'right',
        beforeShow: async () => {
          await clickElement('.electrical-nav .category-card:nth-child(2)')
          await new Promise(r => setTimeout(r, 300))
        }
      },
      // Step 7: 3D visualization (auto-navigated)
      {
        id: 'cell-electrical-3d',
        title: '3D Visualization',
        noScroll: true,
        highlightTarget: '.dimension-stepper .dimension-btn:last-of-type',
        content: `<p>We've switched to the <strong>Surface Plot</strong> (or Bode Plot for impedance) -- a full 3D view of the data.</p>
                  <p class="tour-step-hint">The stepper shows <strong>Value \u2192 2D Plot \u2192 Surface Plot</strong> -- each level adds a dimension.</p>`,
        target: '[data-tour="electrical-dimension-stepper"]',
        position: 'bottom',
        beforeShow: async () => {
          await waitForElement('[data-tour="electrical-dimension-stepper"]')
          await clickElement('.dimension-stepper .dimension-btn:last-of-type')
          await new Promise(r => setTimeout(r, 500))
        }
      },
      // Step 8: Thermal section (auto-navigated)
      {
        id: 'cell-thermal-overview',
        title: 'Thermal Analysis',
        noScroll: true,
        content: `
          <p>The Thermal section follows the same <strong>sidebar + progressive visualization</strong> pattern as Electrical:</p>
          <ul class="tour-step-list">
            <li><strong>Heat Power:</strong> Power dissipated as heat during operation</li>
            <li><strong>Heat Energy:</strong> Total thermal energy per cycle</li>
            <li><strong>Max Temperature:</strong> Peak temperature during discharge</li>
            <li><strong>Efficiency:</strong> Round-trip energy efficiency</li>
          </ul>
          <p class="tour-step-hint">Use the same 1D → 2D → 3D stepper to explore each category in depth.</p>
        `,
        target: '[data-tour="thermal-nav"]',
        position: 'right',
        beforeShow: async () => {
          if (activeSection.value !== 'thermal') {
            switchSection('thermal')
            await new Promise(r => setTimeout(r, 300))
          }
          await waitForElement('[data-tour="thermal-nav"]')
        }
      },
      // Step 9: Remaining sections overview (Mechanical, Chemical, Quality)
      {
        id: 'cell-remaining-sections',
        title: 'More Sections to Explore',
        noScroll: true,
        highlightTarget: '[data-tour="cell-section-tabs"] .section-tab:nth-child(4), [data-tour="cell-section-tabs"] .section-tab:nth-child(5), [data-tour="cell-section-tabs"] .section-tab:nth-child(6)',
        content: `
          <p>Three more sections complete the cell profile:</p>
          <ul class="tour-step-list">
            <li><strong>Mechanical:</strong> Technical drawing, cell dimensions, pole geometry, mass & safety features (CID, PTC, vent)</li>
            <li><strong>Chemical:</strong> Anode & cathode materials, housing material, current collectors</li>
            <li><strong>Quality:</strong> Cell-to-cell variation across a production batch & post-formation capacity loss</li>
          </ul>
          <p class="tour-step-hint">Each section uses flip cards — click <strong>?</strong> on any card to read the detailed definition.</p>
        `,
        target: null,
        position: 'center',
        highlight: false,
        beforeShow: async () => {
          if (activeSection.value !== 'mechanical') {
            switchSection('mechanical')
            await new Promise(r => setTimeout(r, 300))
          }
        }
      },
      // Step 10: Finish
      {
        id: 'cell-compare',
        title: 'Add to Comparison',
        highlightTarget: '.cell-actions .back-button',
        content: `
          <p>You've explored all 6 sections. Found a cell that fits your needs?</p>
          <p>Add it to your <strong>comparison cart</strong> to analyze it alongside other candidates.
          You can compare up to <strong>5 cells</strong> at once, with automatic highlighting of the best
          and worst values across every metric.</p>
          <p class="tour-step-hint">Click the + button next to the section tabs to add this cell.</p>
        `,
        target: null,
        position: 'center',
        buttons: {
          next: 'Finish',
          skip: 'Close'
        }
      }
    ]
  },

  /**
   * COMPARE TOUR
   * Side-by-side cell comparison with sections and view modes
   */
  compare: {
    id: 'compare',
    name: 'Compare View',
    description: 'Analyze cells side-by-side with automatic highlighting',
    icon: 'IconCompare',
    steps: [
      {
        id: 'compare-intro',
        title: 'Cell Comparison',
        content: `
          <p>The Compare view lets you analyze <strong>2 to 5 cells side-by-side</strong> with automatic
          highlighting of the best and worst values for every metric.</p>
          <p>This is where you make final selection decisions based on comprehensive data.</p>
        `,
        target: '[data-tour="compare-container"]',
        position: 'center',
        highlight: false,
        beforeShow: async () => {
          // Ensure at least 3 demo cells are in the compare cart
          const { compareCells, addToCompare } = await import('../state/filterStore.js')
          const { allCells, activeCompareSection, activeCompareView } = await import('../state/globalSignals.js')
          const available = allCells.value.filter(c => c.is_demo)
          const needed = 3 - compareCells.value.length
          if (needed > 0) {
            const toAdd = available
              .filter(c => !compareCells.value.includes(c.id))
              .slice(0, needed)
            toAdd.forEach(c => addToCompare(c.id))
          }

          // Reset to Overview so Step 1 doesn't load heavy Electrical charts
          activeCompareSection.value = 'overview'
          activeCompareView.value = 'table'

          // Navigate to compare page
          if (compareCells.value.length >= 2) {
            await navigateToPage('/compare', '[data-tour="compare-container"]')
          }
        },
        buttons: {
          next: 'Show me the features',
          skip: 'Skip Tour'
        }
      },
      {
        id: 'compare-sections',
        title: 'Section Navigation',
        content: `
          <p>Switch between comparison sections to focus on different aspects:</p>
          <ul class="tour-step-list">
            <li><strong>Overview:</strong> All key metrics in a single table</li>
            <li><strong>Electrical:</strong> DCC and voltage curve overlays</li>
            <li><strong>Thermal:</strong> Temperature-dependent performance comparison</li>
            <li><strong>Mechanical:</strong> Dimensions, weight, and form factor</li>
            <li><strong>Chemical:</strong> Chemistry and material composition</li>
            <li><strong>Quality:</strong> Batch variation and aging comparison</li>
          </ul>
        `,
        target: '[data-tour="compare-section-tabs"]',
        position: 'bottom'
      },
      {
        id: 'compare-table',
        title: 'Comparison Table',
        content: `
          <p>Values are automatically highlighted to help you spot differences:</p>
          <ul class="tour-step-list">
            <li><strong>Green:</strong> Best value (highest energy, lowest aging -- context-aware)</li>
            <li><strong>Red:</strong> Worst value in the group</li>
            <li><strong>Neutral:</strong> Text and categorical values without ranking</li>
          </ul>
          <p class="tour-step-hint">The comparison uses your selected basis, so Gravimetric values compare Wh/kg while Volumetric compares Wh/L.</p>
        `,
        target: '[data-tour="compare-table-area"]',
        position: 'top'
      },
      {
        id: 'compare-views',
        title: 'View Modes',
        content: `
          <p>Sections like Electrical and Thermal offer multiple visualization modes:</p>
          <ul class="tour-step-list">
            <li><strong>2D curves:</strong> Overlay discharge and charge curves for direct comparison</li>
            <li><strong>3D surfaces:</strong> Interactive 3D visualization of multi-dimensional data</li>
          </ul>
          <p class="tour-step-hint">Use the toggle to switch between 2D curve overlays and interactive 3D surface plots.</p>
        `,
        target: '[data-tour="compare-view-toggle"]',
        position: 'bottom',
        beforeShow: async () => {
          // Switch to Electrical section so the view toggle is visible (Overview only has table)
          const { activeCompareSection } = await import('../state/globalSignals.js')
          const { activeCompareView } = await import('../state/globalSignals.js')
          activeCompareSection.value = 'electrical'
          activeCompareView.value = '2d'
          await new Promise(r => setTimeout(r, 400))
          await waitForElement('[data-tour="compare-view-toggle"]')
        }
      },
      {
        id: 'compare-finish',
        title: 'Managing Your Comparison',
        content: `
          <p>You can add or remove cells from the comparison at any time using the Cell Explorer.</p>
          <p>Up to <strong>5 cells</strong> can be compared simultaneously. To change your selection, go back to the
          Explorer and click the compare button on any cell card.</p>
          <p class="tour-step-hint">Your comparison cart is saved across sessions -- you can close the browser and come back later.</p>
        `,
        target: null,
        position: 'center'
      }
    ]
  },

  /**
   * TECHNOLOGY RADAR TOUR (9 steps)
   * Introduces the Technology Radar: Overview cards → Section navigation → Charts → Insights → Controls
   */
  'technology-radar': {
    id: 'technology-radar',
    name: 'Technology Radar',
    description: 'Explore battery technology trends, chemistry evolution, and performance insights',
    icon: 'IconMap',
    steps: [
      {
        id: 'radar-intro',
        title: 'Technology Radar',
        content: `
          <p>The <strong>Technology Radar</strong> tracks how battery technology is evolving -- from chemistry shifts
          to performance frontiers.</p>
          <p>Each section tells a data-driven story with interactive charts, key insights, and year-over-year trends.</p>
        `,
        target: null,
        position: 'center',
        highlight: false,
        beforeShow: async () => {
          await navigateToPage('/radar', '[data-tour="radar-overview-cards"]')
        },
        buttons: {
          next: 'Show me around',
          skip: 'Skip Tour'
        }
      },
      {
        id: 'radar-story-cards',
        title: 'Story Cards',
        content: `
          <p>Each card represents a <strong>research story</strong> with multiple slides of charts and insights:</p>
          <ul class="tour-step-list">
            <li><strong>Chemistry Evolution:</strong> How cathode and anode chemistries are shifting</li>
            <li><strong>Performance Limits:</strong> Energy and power density frontiers over time</li>
            <li><strong>Na-Ion vs LFP:</strong> How sodium-ion compares to the established LFP benchmark</li>
          </ul>
          <p class="tour-step-hint">Click any card to dive into its story.</p>
        `,
        target: '[data-tour="radar-overview-cards"]',
        position: 'top'
      },
      {
        id: 'radar-section-tabs',
        title: 'Section Navigation',
        content: `
          <p>Use these tabs to <strong>jump between stories</strong> without going back to the overview.</p>
          <p>Each tab is color-coded to match its story card.</p>
        `,
        target: '[data-tour="radar-section-tabs"]',
        position: 'bottom'
      },
      {
        id: 'radar-enter-section',
        title: 'Inside a Section',
        content: `
          <p>Each section is organized as a <strong>slide deck</strong> -- a sequence of charts with a key insight per slide.</p>
          <p>Navigate with arrow keys, dot indicators, or the slide controls in the top bar.</p>
        `,
        target: null,
        position: 'center',
        highlight: false,
        beforeShow: async () => {
          await navigateToPage('/radar/chemistry-evolution', '[data-tour="radar-section-page"]')
          radarActivePlotIndex.value = 0
          await new Promise(r => setTimeout(r, 500))
        }
      },
      {
        id: 'radar-chart-area',
        title: 'Interactive Charts',
        content: `
          <p>Charts are fully interactive:</p>
          <ul class="tour-step-list">
            <li><strong>Hover</strong> over data points to see cell details</li>
            <li><strong>Click</strong> a data point to open that cell's detail page</li>
            <li><strong>Zoom</strong> by scrolling or box-selecting a region</li>
          </ul>
          <p class="tour-step-hint">Dual-chart slides show two complementary views side by side.</p>
        `,
        target: '[data-tour="radar-plot-container"]',
        position: 'bottom',
        beforeShow: async () => {
          await waitForElement('[data-tour="radar-plot-container"]', { timeout: 8000 })
        }
      },
      {
        id: 'radar-key-insight',
        title: 'Key Insights',
        content: `
          <p>Below each chart, a <strong>data-driven insight</strong> summarizes what the visualization reveals.</p>
          <p>Insights are computed from the actual data -- percentages, trends, and comparisons update automatically.</p>
        `,
        target: '[data-tour="radar-key-insight"]',
        position: 'top',
        beforeShow: async () => {
          await waitForElement('[data-tour="radar-key-insight"]')
        }
      },
      {
        id: 'radar-slide-nav',
        title: 'Slide Navigation',
        content: `
          <p>Use the <strong>dot indicators</strong> and arrows to navigate between slides within a section.</p>
          <p>You can also use the <strong>left/right arrow keys</strong> on your keyboard.</p>
          <p class="tour-step-hint">At the last slide, the right arrow takes you to the next section automatically.</p>
        `,
        target: '[data-tour="radar-slide-nav"]',
        position: 'bottom',
        beforeShow: async () => {
          await waitForElement('[data-tour="radar-slide-nav"]')
        }
      },
      {
        id: 'radar-basis-toggle',
        title: 'Wh/kg \u2194 Wh/L',
        content: `
          <p>Switch between <strong>gravimetric</strong> (Wh/kg, W/kg) and <strong>volumetric</strong> (Wh/L, W/L) views.</p>
          <p>All charts in the current section update instantly when you toggle the basis.</p>
          <p class="tour-step-hint">Gravimetric is key for weight-sensitive applications (EVs), volumetric for space-constrained designs.</p>
        `,
        target: '[data-tour="radar-basis-toggle"]',
        position: 'bottom',
        beforeShow: async () => {
          await waitForElement('[data-tour="radar-basis-toggle"]')
        }
      },
      {
        id: 'radar-finish',
        title: 'Explore the Radar',
        content: `
          <p>You're all set! The Technology Radar is updated regularly as new cells are added to the database.</p>
          <p>Explore the stories at your own pace -- each section has <strong>2--3 slides</strong> with unique charts and insights.</p>
          <p class="tour-step-hint">Use the Help menu (?) to replay this tour anytime.</p>
        `,
        target: null,
        position: 'center',
        highlight: false,
        beforeShow: async () => {
          await navigateToPage('/radar', '[data-tour="radar-overview-cards"]')
        },
        buttons: {
          next: 'Finish',
          skip: 'Close'
        }
      }
    ]
  }
}

/**
 * Get tour by ID
 * @param {string} tourId
 * @returns {Tour | null}
 */
export function getTour(tourId) {
  return TOURS[tourId] || null
}

/**
 * Get all available tours
 * @returns {Tour[]}
 */
export function getAllTours() {
  return Object.values(TOURS)
}

/**
 * Check if tour exists
 * @param {string} tourId
 * @returns {boolean}
 */
export function tourExists(tourId) {
  return tourId in TOURS
}

/**
 * Get available tours for suggestions (excludes welcome tour)
 * Used by the What's Next panel and future Help menu.
 * @returns {Array<{id: string, name: string, description: string, icon: string}>}
 */
export function getAvailableTours() {
  return Object.values(TOURS)
    // 'welcome' has its own onboarding slot; 'ion-intro' is an auto-triggered
    // first-run demo (Task 2), not a tour a user picks from a list.
    .filter(tour => tour.id !== 'welcome' && tour.id !== 'ion-intro')
    .map(tour => ({
      id: tour.id,
      name: tour.name,
      description: tour.description,
      icon: tour.icon
    }))
}

/**
 * Get all tours including Welcome — for the Settings management view.
 * Excludes ion-intro for the same reason as getAvailableTours() — it has its
 * own dedicated "Meet Ion" replay entry (HelpMenuButton.jsx), not a spot in
 * the generic tour list.
 * @returns {Array<{id: string, name: string, description: string, icon: string}>}
 */
export function getAllToursForSettings() {
  return Object.values(TOURS)
    .filter(tour => tour.id !== 'ion-intro')
    .map(tour => ({
      id: tour.id,
      name: tour.name,
      description: tour.description,
      icon: tour.icon,
    }))
}

/**
 * Restore the Explorer state captured at ion-intro start (see
 * `_ionIntroBaseline` above), undoing the intro-drive demo's filter/sort
 * mutations.
 *
 * FIX 2 (S4 fix-wave, BA decision): intro-handover's own beforeShow no
 * longer calls this — the hero moment is a REAL Ion turn now, and its
 * result must stay on screen across the handover instead of being quietly
 * reverted (see that beforeShow for the full reasoning). It still nulls
 * `_ionIntroBaseline`/`_ionIntroReturnPath` directly (without restoring),
 * so this function stays a no-op for the REST of a run that reached
 * handover.
 *
 * The only remaining callers are tourState.js's completeTour()/skipTour()
 * ion-intro special-case (via a dynamic import — mirrors the existing
 * technology-radar special-case, which dynamically imports radarState.js
 * for the same reason: keeps tourState.js free of a static import of this
 * module), where it now acts PURELY as a no-op-safe fallback for a skip/ESC
 * that happens BEFORE reaching intro-handover (e.g. during intro-greet/
 * intro-drive) — that is the only path left where the pre-demo baseline
 * genuinely still needs restoring: the user bailed before any real Ion
 * result existed to preserve.
 *
 * M3: also navigates back to `_ionIntroReturnPath` (the pathname+search
 * captured at intro-greet, before it moved the user to /explorer) if it
 * differs from where restoreExplorerState() above just left the URL —
 * restoreExplorerState() only restores the `?view=` query param onto
 * whatever pathname is CURRENT, not the original pathname itself (that
 * snapshot shape is shared with the live Ion transaction revert/commit path
 * and deliberately not changed for this onboarding-only need).
 */
export function restoreIonIntroBaseline() {
  // S4/F3: covers the teardown paths (skip / ESC / complete) that do not go
  // through intro-handover. Guarded on liveness inside, so a completed tour
  // never aborts a stream the USER started.
  abortIonIntroTurnIfLive()
  if (!_ionIntroBaseline) return
  restoreExplorerState(_ionIntroBaseline)
  _ionIntroBaseline = null

  const returnPath = _ionIntroReturnPath
  _ionIntroReturnPath = null
  if (returnPath && returnPath !== window.location.pathname + window.location.search) {
    navigate(returnPath, { replace: true })
  }
}

/**
 * Map current location/view to the most relevant tour ID
 * Used by HelpMenu to highlight the contextual tour
 * @param {string} location - Current wouter-preact location path
 * @param {string} viewMode - Current appViewMode value (e.g., 'cells-grid', 'cells-graph')
 * @returns {string|null} Tour ID or null
 */
export function getTourIdForLocation(location, viewMode) {
  if (location.startsWith('/radar')) return 'technology-radar'
  if (location.startsWith('/cell-selection') || location.startsWith('/benchmarking')) return 'benchmarking'
  if (location.startsWith('/compare')) return 'compare'
  // For explorer sub-modes, check the passed viewMode
  if (viewMode === 'cells-graph') return 'graph'
  if (viewMode === 'cells-single') return 'single-cell'
  // Default explorer views (grid/table)
  return 'explorer-grid'
}
